import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Landmark, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice, getSessionId } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, clearCart, sessionId } = useCart();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('carte_total');
  const [customer, setCustomer] = useState({
    email: '',
    nom: '',
    prenom: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    taille_cycliste: ''
  });

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // Load saved customer data from localStorage
    const savedCustomer = localStorage.getItem('belleville_customer');
    if (savedCustomer) {
      try {
        const parsed = JSON.parse(savedCustomer);
        setCustomer(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        // Error handled silently
      }
    }
    
    axios.post(`${API}/visitor/log`, {
      page: 'checkout',
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer
    }).catch(() => {});
  }, []);

  const handleInputChange = (e) => {
    const newCustomer = { ...customer, [e.target.name]: e.target.value };
    setCustomer(newCustomer);
    // Save to localStorage for future visits
    localStorage.setItem('belleville_customer', JSON.stringify(newCustomer));
  };

  const calculateAmountDue = () => {
    if (paymentMethod === 'carte_acompte') {
      return cart.total * 0.5;
    }
    return cart.total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // First, ensure cart is saved
      await axios.put(`${API}/cart/${sessionId}`, {
        session_id: sessionId,
        items: cart.items
      });

      // Get cart ID
      const cartResponse = await axios.get(`${API}/cart/${sessionId}`);
      const cartId = cartResponse.data.id;

      if (!cartId) {
        throw new Error('Panier non trouvé');
      }

      // Create order
      const orderResponse = await axios.post(`${API}/orders`, {
        cart_id: cartId,
        customer: customer,
        payment_method: paymentMethod
      });

      const orderId = orderResponse.data.id;

      if (paymentMethod === 'virement') {
        // Redirect to bank transfer instructions
        navigate(`/checkout/virement?order_id=${orderId}`);
      } else {
        // Create Stripe checkout session
        const hostUrl = window.location.origin + '/';
        const checkoutResponse = await axios.post(
          `${API}/checkout/session?order_id=${orderId}&host_url=${encodeURIComponent(hostUrl)}`
        );

        // Redirect to Stripe
        window.location.href = checkoutResponse.data.url;
      }
    } catch (error) {
      // Error handled silently
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="font-body text-muted-foreground mb-4">
            {language === 'fr' ? 'Votre panier est vide' : 'Your cart is empty'}
          </p>
          <Button onClick={() => navigate('/')} variant="outline" className="rounded-none">
            <ArrowLeft className="mr-2 w-4 h-4" />
            {language === 'fr' ? 'Retour à la boutique' : 'Back to shop'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'fr' ? 'Retour' : 'Back'}
          </button>

          <h1 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight">
            {language === 'fr' ? 'Finaliser la commande' : 'Complete your order'}
          </h1>
        </motion.div>

        <form onSubmit={handleSubmit} className="mt-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Customer Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading font-bold text-xl uppercase mb-6">
                  {language === 'fr' ? 'Vos coordonnées' : 'Your details'}
                </h2>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="prenom" className="font-mono text-xs uppercase tracking-widest">
                        {language === 'fr' ? 'Prénom' : 'First name'}
                      </Label>
                      <Input
                        id="prenom"
                        name="prenom"
                        value={customer.prenom}
                        onChange={handleInputChange}
                        className="mt-2 rounded-none"
                        required
                        data-testid="checkout-prenom"
                      />
                    </div>
                    <div>
                      <Label htmlFor="nom" className="font-mono text-xs uppercase tracking-widest">
                        {language === 'fr' ? 'Nom' : 'Last name'}
                      </Label>
                      <Input
                        id="nom"
                        name="nom"
                        value={customer.nom}
                        onChange={handleInputChange}
                        className="mt-2 rounded-none"
                        required
                        data-testid="checkout-nom"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={customer.email}
                      onChange={handleInputChange}
                      className="mt-2 rounded-none"
                      required
                      data-testid="checkout-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="telephone" className="font-mono text-xs uppercase tracking-widest">
                      {language === 'fr' ? 'Téléphone' : 'Phone'}
                    </Label>
                    <Input
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={customer.telephone}
                      onChange={handleInputChange}
                      className="mt-2 rounded-none"
                      required
                      data-testid="checkout-telephone"
                    />
                  </div>

                  <div>
                    <Label htmlFor="adresse" className="font-mono text-xs uppercase tracking-widest">
                      {language === 'fr' ? 'Adresse' : 'Address'}
                    </Label>
                    <Input
                      id="adresse"
                      name="adresse"
                      value={customer.adresse}
                      onChange={handleInputChange}
                      className="mt-2 rounded-none"
                      required
                      data-testid="checkout-adresse"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="code_postal" className="font-mono text-xs uppercase tracking-widest">
                        {language === 'fr' ? 'Code postal' : 'Postal code'}
                      </Label>
                      <Input
                        id="code_postal"
                        name="code_postal"
                        value={customer.code_postal}
                        onChange={handleInputChange}
                        className="mt-2 rounded-none"
                        required
                        data-testid="checkout-code-postal"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ville" className="font-mono text-xs uppercase tracking-widest">
                        {language === 'fr' ? 'Ville' : 'City'}
                      </Label>
                      <Input
                        id="ville"
                        name="ville"
                        value={customer.ville}
                        onChange={handleInputChange}
                        className="mt-2 rounded-none"
                        required
                        data-testid="checkout-ville"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="taille_cycliste" className="font-mono text-xs uppercase tracking-widest">
                      {language === 'fr' ? 'Votre taille (cm) - optionnel' : 'Your height (cm) - optional'}
                    </Label>
                    <Input
                      id="taille_cycliste"
                      name="taille_cycliste"
                      placeholder="Ex: 175"
                      value={customer.taille_cycliste}
                      onChange={handleInputChange}
                      className="mt-2 rounded-none"
                      data-testid="checkout-taille"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <h2 className="font-heading font-bold text-xl uppercase mb-6">
                  {language === 'fr' ? 'Mode de paiement' : 'Payment method'}
                </h2>
                <RadioGroup 
                  value={paymentMethod} 
                  onValueChange={setPaymentMethod}
                  className="space-y-4"
                >
                  <div 
                    className={`border p-4 cursor-pointer transition-all ${
                      paymentMethod === 'carte_total' ? 'border-black border-2' : 'border-border'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="carte_total" id="carte_total" data-testid="payment-carte-total" />
                      <Label htmlFor="carte_total" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-5 h-5" />
                          <div>
                            <span className="font-heading font-bold uppercase block">
                              {language === 'fr' ? 'Carte bancaire - Paiement total' : 'Credit card - Full payment'}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {language === 'fr' ? `Payer ${formatPrice(cart.total)} maintenant` : `Pay ${formatPrice(cart.total)} now`}
                            </span>
                          </div>
                        </div>
                      </Label>
                    </div>
                  </div>

                  <div 
                    className={`border p-4 cursor-pointer transition-all ${
                      paymentMethod === 'carte_acompte' ? 'border-black border-2' : 'border-border'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="carte_acompte" id="carte_acompte" data-testid="payment-carte-acompte" />
                      <Label htmlFor="carte_acompte" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-5 h-5" />
                          <div>
                            <span className="font-heading font-bold uppercase block">
                              {language === 'fr' ? 'Carte bancaire - Acompte 50%' : 'Credit card - 50% deposit'}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {language === 'fr' 
                                ? `Payer ${formatPrice(cart.total * 0.5)} maintenant, solde à la livraison`
                                : `Pay ${formatPrice(cart.total * 0.5)} now, balance on delivery`}
                            </span>
                          </div>
                        </div>
                      </Label>
                    </div>
                  </div>

                  <div 
                    className={`border p-4 cursor-pointer transition-all ${
                      paymentMethod === 'virement' ? 'border-black border-2' : 'border-border'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="virement" id="virement" data-testid="payment-virement" />
                      <Label htmlFor="virement" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <Landmark className="w-5 h-5" />
                          <div>
                            <span className="font-heading font-bold uppercase block">
                              {language === 'fr' ? 'Virement bancaire' : 'Bank transfer'}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {language === 'fr' ? 'Validé manuellement après réception' : 'Manually validated after reception'}
                            </span>
                          </div>
                        </div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Order Summary */}
            <div>
              <div className="bg-secondary p-6 sticky top-24">
                <h2 className="font-heading font-bold text-xl uppercase mb-6">
                  {language === 'fr' ? 'Récapitulatif' : 'Summary'}
                </h2>

                <div className="space-y-4 mb-6">
                  {cart.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-start">
                      <div>
                        <span className="font-heading font-bold text-sm uppercase block">{item.product_name}</span>
                        {item.config && (
                          <span className="font-mono text-xs text-muted-foreground">
                            {item.config.taille && `${language === 'fr' ? 'Taille' : 'Size'} ${item.config.taille}`}
                            {item.config.couleur && ` • ${item.config.couleur}`}
                          </span>
                        )}
                        <span className="font-mono text-xs text-muted-foreground block">
                          {language === 'fr' ? 'Qté' : 'Qty'}: {item.quantity}
                        </span>
                      </div>
                      <span className="font-mono font-bold">{formatPrice(item.total_price)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="font-body text-muted-foreground">
                      {language === 'fr' ? 'Sous-total' : 'Subtotal'}
                    </span>
                    <span className="font-mono">{formatPrice(cart.total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body text-muted-foreground">
                      {language === 'fr' ? 'Livraison' : 'Shipping'}
                    </span>
                    <span className="font-mono">{language === 'fr' ? 'Offerte' : 'Free'}</span>
                  </div>
                  {paymentMethod === 'carte_acompte' && (
                    <div className="flex justify-between text-[#0047AB]">
                      <span className="font-body">{language === 'fr' ? 'Acompte (50%)' : 'Deposit (50%)'}</span>
                      <span className="font-mono font-bold">{formatPrice(cart.total * 0.5)}</span>
                    </div>
                  )}
                </div>

                <div className="border-t border-border pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-heading font-bold uppercase">
                      {paymentMethod === 'carte_acompte' 
                        ? (language === 'fr' ? 'À payer maintenant' : 'Pay now')
                        : 'Total'}
                    </span>
                    <span className="font-mono text-2xl font-bold">{formatPrice(calculateAmountDue())}</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 mt-6 rounded-none font-mono uppercase tracking-widest"
                  data-testid="checkout-submit"
                >
                  {loading 
                    ? (language === 'fr' ? 'Traitement...' : 'Processing...') 
                    : (paymentMethod === 'virement' 
                        ? (language === 'fr' ? 'Valider la commande' : 'Confirm order')
                        : (language === 'fr' ? 'Payer maintenant' : 'Pay now')
                      )
                  }
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
